import { formatDate } from '@angular/common';
import { Injectable } from '@angular/core';
import { Locale, add, format, parseISO } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { ITimeLabel } from '@app/checkout/guest-details/gift-card-form/time-label.model';
import { CDateImportList, TDateFnsLocaleCode } from '@core/date/date-import-list';

@Injectable({
  providedIn: 'root'
})
export class DateFormatService {
  private apiDateConversionLocale = 'en-US';
  public venueLocale: Locale = enUS;

  /**
   * Converts js date object to js locale.
   * @param date takes a js date object.
   * @returns date string formatted to browser locale e.g. '2022-01-24'.
   */
  dateToStringDateOnlyHyphen(date: Date | string): string {
    return formatDate(date, 'yyyy-MM-dd', this.apiDateConversionLocale);
  }

  /**
   * Converts a date only string to a js date object.
   * @param dateStr date only string with hyphens - '2022-01-24'.
   * @returns date object at 00:00:00 in the local timezone of the system.
   */
  dateOnlyStringToDate(dateStr: string | undefined): Date {
    return new Date(`${dateStr}T00:00:00`);
  }

  /**
   * Removes the time element from a js date object.
   * @param date date object.
   * @returns date object at 00:00:00 in the local timezone of the system.
   */
  getDateWithoutTime(date: Date | undefined): Date {
    if (!date) {
      date = new Date();
    }

    return new Date(date.setHours(0, 0, 0, 0));
  }

  /** Takes a string (or date) and converts to new Date. */
  dateStringToDate(dateStr: string | undefined): Date {
    if (!dateStr) {
      return new Date();
    }

    return parseISO(dateStr);
  }

  /**
   * Checks if the supplied date is the same.
   * @param firstDate js date object.
   * @param secondDate js date object to compare with.
   * @returns true if the supplied date is current date.
   */
  isSameDay(firstDate: Date | undefined | string, secondDate: Date | undefined | string): boolean {
    if (!firstDate || !secondDate) {
      return false;
    }

    if (typeof firstDate === 'string') {
      firstDate = parseISO(firstDate);
    }

    if (typeof secondDate === 'string') {
      secondDate = parseISO(secondDate);
    }

    return (
      firstDate.getDate() === secondDate.getDate() &&
      firstDate.getMonth() === secondDate.getMonth() &&
      firstDate.getFullYear() === secondDate.getFullYear()
    );
  }

  /** Check if the two dates are in the same month. */
  isSameMonth(firstDate: Date | undefined | string, secondDate: Date | undefined | string): boolean {
    if (!firstDate || !secondDate) {
      return false;
    }

    if (typeof firstDate === 'string') {
      firstDate = parseISO(firstDate);
    }

    if (typeof secondDate === 'string') {
      secondDate = parseISO(secondDate);
    }

    return firstDate.getMonth() === secondDate.getMonth() && firstDate.getFullYear() === secondDate.getFullYear();
  }

  /** Check if the second date (as midnight) occurs before the first. */
  isDayBeforeFirstDate(firstDate: Date | undefined | string, secondDate: Date | undefined | string): boolean {
    if (!firstDate || !secondDate) {
      return false;
    }

    if (typeof firstDate === 'string') {
      firstDate = parseISO(firstDate);
    }

    if (typeof secondDate === 'string') {
      secondDate = parseISO(secondDate);
    }

    firstDate = new Date(firstDate.setHours(0, 0, 0, 0));
    secondDate = new Date(secondDate.setHours(0, 0, 0, 0));

    return secondDate.getTime() - firstDate.getTime() < 0;
  }

  /** Process date or dateStr to return Monday */
  dayOfWeekString(date: Date | string | undefined): string {
    if (!date) {
      return '';
    }

    if (typeof date === 'string') {
      date = parseISO(date);
    }

    return format(date, 'EEEE', { locale: this.venueLocale });
  }

  /** Process date or dateStr to return Mon */
  shortDayOfWeekString(date: Date | string | undefined): string {
    if (!date) {
      return '';
    }

    if (typeof date === 'string') {
      date = parseISO(date);
    }

    return format(date, 'EEE', { locale: this.venueLocale });
  }

  /** Process date or dateStr to return Mon, 1 Nov 2021. */
  dayDateString(date: Date | string | undefined): string {
    if (!date) {
      return '';
    }

    if (typeof date === 'string') {
      date = parseISO(date);
    }

    return format(date, 'EEE, dd MMM yyyy', { locale: this.venueLocale });
  }

  /** Process date or dateStr to return 21 Nov 21 (or optionally 0-4 year digits) */
  shortDateString(date: Date | string | undefined, yearDigits = 2): string {
    if (!date) {
      return '';
    }

    if (typeof date === 'string') {
      date = parseISO(date);
    }

    const yearFormat = yearDigits > 0 ? ` ${'y'.repeat(yearDigits)}` : '';
    return format(date, `dd MMM${yearFormat}`, { locale: this.venueLocale });
  }

  /**
   * Add any number of days to a base date.
   * @returns day Date String.
   */
  addDays(date: Date, days: number | undefined): Date {
    const result = new Date(date);
    result.setDate(date.getDate() + (days || 0));
    return result;
  }

  /**
   * Add any number of years to a base date.
   * @returns date object.
   */
  addYears(date: Date, years: number | undefined): Date {
    const result = new Date(date);
    result.setFullYear(date.getFullYear() + (years || 0));

    return result;
  }

  /**
   * Converts standard date object to dateIndex to pass to API.
   * @param date date object or string or undefined.
   * @returns number in format 20210104 for yyyyMMdd as number or string.
   */
  toDateIndex(date: Date | string): number {
    const result = formatDate(date, 'yyyyMMdd', this.apiDateConversionLocale);
    return parseInt(result, 10);
  }

  /**
   * Converts a 8 digit dateIndex as to a standard date object
   * @param date string or number or undefined - 20220820
   * @returns Date object or undefined if invalid date passed
   */
  dateIndexToDate(date: string | number | undefined): undefined | Date {
    date = typeof date === 'number' ? date.toString() : date;
    if (typeof date !== 'string' || date.length !== 8 || !parseInt(date)) {
      return undefined;
    }

    const year = parseInt(date.substring(0, 4), 10);
    const month = parseInt(date.substring(4, 6), 10) - 1; // corrects index
    const day = parseInt(date.substring(6, 8), 10);

    return new Date(year, month, day);
  }

  /**
   * Add any number of minutes to an hour string in the format 10:00.
   * @returns hour string
   */
  addMinutes(time: string, minutes: number): string {
    const date = new Date();

    date.setHours(parseInt(time.split(':')[0]));
    date.setMinutes(parseInt(time.split(':')[1]) + minutes);

    const newHourNumber = date.getHours() * 100 + date.getMinutes();
    return this.numberToHourString(newHourNumber);
  }

  /** Helper to remove format '09:00' as 900. */
  hourStringToNumber(hour?: string): number {
    if (!hour) {
      return 0;
    }

    const hourResult = parseInt(hour.replace(':', ''), 10);

    return Number.isNaN(hourResult) ? 0 : hourResult;
  }

  /** Modify a startTime of 800 or 1400 to '08:00' or '14:00'. Assist in converting startTime of date. */
  numberToHourString(hour: number): string {
    let hourString = hour.toString();
    hourString = hourString.padStart(4, '0');

    return `${hourString.slice(0, 2)}:${hourString.slice(2, 4)}`;
  }

  /**
   * A session has a date string and a time string separately.
   * @param date string format date such as '24-02-2022'
   * @param startTime string format 24hr time such as '14:00'
   * @returns Date object
   */
  sessionDateToDate(date: string, startTime: string): Date {
    return new Date(`${date}T${startTime}:00`);
  }

  /** Creates a list of hour number with related display time starting at 700/'7:00 AM' */
  populateTimeToSendList(startTime: number, length: number): ITimeLabel[] {
    const timeValues = Array.from(Array(length), (_, x) => x * 100 + startTime);
    const timeList: ITimeLabel[] = [];

    timeValues.forEach((value: number) => {
      const isAM = value < 1200;

      let labelValue = value.toString();
      if (!isAM) {
        const pmValue = value - 1200;
        labelValue = (pmValue || 1200).toString();
      }

      timeList.push({
        value: value,
        label: `${labelValue.slice(0, -2)}:${labelValue.slice(-2)} ${isAM ? 'AM' : 'PM'}`
      });
    });

    return timeList;
  }

  /**
   * Checks a date plus a drift against current time for 'freshness'.
   * @param olderDate date to test if fresh as js Date object.
   * @param maxAge the time difference to now that is allowed in milliseconds - e.g. 60000 for 10min.
   * @returns is fresh if the date + maxAge is less that current time in milliseconds.
   */
  isDateFresh(olderDate: Date | string, maxAge: number): boolean {
    if (typeof olderDate === 'string') {
      olderDate = new Date(olderDate);
    }

    const now = new Date().getTime();
    const delta = olderDate.getTime() + maxAge - now;

    return delta > 0;
  }

  /**
   * Venue timezone in VenueSettings is represented as a string number as '10' for UTC+10
   * new Date() gets the browsers local time, and .getTimeZoneOffset() returns the -offset in minutes.
   * Therefore we can calculate the browsers current time in relation to the venue
   * for example the browser is in UTC+10 (at 9:52am) and the venue is in UTC+8 (at 7:52am).
   * @returns related venueTime in ms.
   */
  venueTimeMs(venueTimeZoneOffsetMin: number, date?: Date): number {
    const today = date || new Date();
    const browserTimeZoneOffset = today.getTimezoneOffset();
    const msDifference = (-venueTimeZoneOffsetMin - browserTimeZoneOffset) * 60 * 1000;
    return today.getTime() - msDifference;
  }

  /** Gets the current venue time as a date. */
  currentVenueTime(venueTimeZoneOffsetMin: number, date?: Date): Date {
    return new Date(this.venueTimeMs(venueTimeZoneOffsetMin, date));
  }

  /**
   * Takes the venues time zone offset at a time, 1130 and returns offset in minutes as 690
   * this matches output of native js date object - getTimezoneOffset()
   */
  calcTimeZoneOffsetMinutes(timeZoneOffset: number): number {
    const hours = Math.trunc(timeZoneOffset / 100);
    const minutes = timeZoneOffset - hours * 100;

    return hours * 60 + minutes;
  }

  /**
   * With input of '2023-09-17' and timezone of 10 expect the string converted to a date and
   * return local time equivalent on the venues supplied hour string
   */
  calcVenueTimeFromDateOnly(dateOnlyString: string, venueTimeZoneOffsetMin: number, hour = '00:00'): number {
    // The hour at local time
    const dateStr = new Date(`${dateOnlyString}T${hour}:00.000`);
    const browserTimeZoneOffset = dateStr.getTimezoneOffset();
    // effectively remove the browsers offset (to UTC) and then add the venues offset
    const addVenueTimeZone = dateStr.getTime() - (browserTimeZoneOffset + venueTimeZoneOffsetMin) * 60 * 1000;

    return addVenueTimeZone;
  }

  /**
   * Calculate time with am/pm from date.
   * Expect 8:00pm - notice lack of space and lowercase or leading 0.
   */
  calcMeridiem(date: Date | number | string): string {
    if (typeof date === 'string') {
      date = parseISO(date);
    } else if (typeof date === 'number') {
      date = new Date(date);
    }

    const options: Intl.DateTimeFormatOptions = {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    };
    return date.toLocaleString('en-US', options).toLocaleLowerCase().replace(/\s/g, '');
  }

  /** Converts time number(1100) to time string ('11:00') */
  convertTimeNumberToString(time: number | undefined): string {
    if (!time) {
      return '';
    }

    const result = String(time);
    return result.slice(0, result.length - 2) + ':' + result.slice(result.length - 2);
  }

  /** Converts expiry month (3) and expiry year (2030) to card format (03/30) */
  convertToBankCardExpiry(month: string | undefined, year: string | undefined): string {
    if (!month || !year) {
      return '';
    }

    return ('0' + month).slice(-2) + '/' + year.slice(-2);
  }

  /** Check if credit card is expired. */
  isExpired(month: string | number, year: string | number): boolean {
    const today = new Date();
    const expiry = new Date();
    expiry.setFullYear(+year, +month, 0);

    return expiry < today;
  }

  /** Uses the date-fsn format to format to the supplied standard pattern, e.g. 'MMMM yyyy' */
  formatDateVenueLocale(date: Date, formatPattern: string): string {
    return format(date, formatPattern, { locale: this.venueLocale });
  }

  /** Use Date Fns to add a time string '13:30' to a date (expecting date only object) */
  addTimeToDate(date: Date, time: string | undefined): Date {
    date = this.getDateWithoutTime(date);
    const startSplit: number[] = time?.split(':').map((value: string) => Number(value)) || [0, 0];
    return add(date, { hours: startSplit[0], minutes: startSplit[1] });
  }

  isValidDate(date?: Date): date is Date {
    return !!date && !isNaN(date.getTime());
  }

  /** Returns today translation key if the date is today, otherwise returns the date in the format 'Mon, 1 Nov 2021'. */
  todayOrDayDateString(date: Date | string): string {
    if (this.isSameDay(date, new Date())) {
      return 'GENERIC.TODAY';
    }

    return this.dayDateString(date);
  }

  /**
   * Sets the venue locale for the app.
   * Assuming the country or dateTime locale is 'es', 'en-AU', 'gb'
   * The lang set in the browser url will over-ride the venue date settings
   */
  async setVenueLocale(dateTimeLocale: string, lang: string): Promise<Locale> {
    let [locale, region] = dateTimeLocale.split('-');
    if (lang.includes('-')) {
      [locale, region] = lang.split('-');
    } else {
      locale = lang;
    }

    this.venueLocale = await this.getDateFnsLocale(locale, region);
    return this.venueLocale;
  }

  /** Gets the locale from the entire date-fns object */
  private async getDateFnsLocale(locale: string, region?: string): Promise<Locale> {
    if (region) {
      region = region.toUpperCase();
    }

    const baseLocale = locale.substring(0, 2);
    const imports = [this.importDateFnsLocale(baseLocale)];
    if (region) {
      const regionLocale = baseLocale + '-' + region;
      imports.unshift(this.importDateFnsLocale(regionLocale));
    }

    const locales = await Promise.all(imports);
    return locales[0] ?? locales[1] ?? enUS;
  }

  private async importDateFnsLocale(dateFnsLocale: string): Promise<Locale | null> {
    try {
      const importFn = CDateImportList[dateFnsLocale as TDateFnsLocaleCode];
      const localeModule = await importFn();
      return localeModule.default as Locale;
    } catch (error) {
      return null;
    }
  }
}
